import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store';

import i18n from '@/plugins/i18n';
import getStartingLocale from '@/utils/i18n/get-starting-locale';
import { supportedLocalesInclude } from '@/utils/i18n/supported-locales';
import {
  setDocumentDirectionPerLocale,
  setDocumentLang,
} from '@/utils/i18n/document';

import auth from '@/middleware/auth';
import agility from '@/middleware/agility';
import director from '@/middleware/director';
import invoice from '@/middleware/invoice';
import devices from '@/middleware/devices';
import activity from '@/middleware/activity';

import isAdmin from '@/middleware/isAdmin';
import cguAccept from '@/middleware/cguAccept';


import backup from '@/middleware/backup';

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/:locale/login',
      redirect: { name: 'login' },
      component: () => import('./views/PageLogin.vue'),
      children: [
        {
          path: '',
          name: 'login',
          component: () => import('./views/Login.vue'),
          meta: {
            title: 'meta.login',
          },
        },
        {
          path: 'forgotten-password',
          name: 'forgottenPassword',
          component: () => import('./views/ForgottenPassword.vue'),
          meta: {
            title: 'meta.forgottenPassword',
          },
        },
        {
          path: 'create-password/:uidb64/:token',
          name: 'createPassword',
          component: () => import('./views/CreatePassword.vue'),
          meta: {
            title: 'meta.createPassword',
          },
        },
        {
          path: 'reset-password/:uidb64/:token',
          name: 'resetPassword',
          component: () => import('./views/CreatePassword.vue'),
          meta: {
            title: 'meta.resetPassword',
          },
        },
      ],
    },
    {
      path: '/',
      redirect: i18n.locale,
    },
    {
      path: '/:locale',
      redirect: { name: 'dashboard' },
      component: () => import('./views/Page.vue'),
      children: [
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import('./views/Dashboard.vue'),
          meta: {
            title: 'meta.dashboard',
            middleware: auth,
          },
        },
        {
          path: 'agility/monitoring',
          name: 'agilityMonitoring',
          component: () => import('./views/AgilityMonitoring.vue'),
          meta: {
            title: 'meta.agility',
            subtitle: 'meta.monitoring',
            middleware: [agility, cguAccept],
          },
        },
        {
          path: 'agility/vms',
          name: 'agilityVms',
          component: () => import('./views/AgilityVms.vue'),
          meta: {
            title: 'meta.agility',
            subtitle: 'meta.vms',
            middleware: [agility, cguAccept],
          },
        },
        {
          path: 'director/pools',
          name: 'directorPools',
          component: () => import('./views/DirectorPools.vue'),
          meta: {
            title: 'meta.director',
            subtitle: 'meta.pools',
            middleware: [director, cguAccept],
          },
        },
        {
          path: 'director/vms',
          name: 'directorVms',
          component: () => import('./views/DirectorVms.vue'),
          meta: {
            title: 'meta.director',
            subtitle: 'meta.vms',
            middleware: [director, cguAccept],
          },
        },
        {
          path: 'director/edges',
          name: 'directorEdges',
          component: () => import('./views/DirectorEdges.vue'),
          meta: {
            title: 'meta.director',
            subtitle: 'meta.edges',
            middleware: [director, cguAccept],
          },
        },
        {
          path: 'backup',
          name: 'backup',
          component: () => import('./views/Backup.vue'),
          meta: {
            title: 'meta.backup',
            middleware: [backup, cguAccept, activity],
          },
        },
        {
          path: 'devices',
          name: 'devices',
          component: () => import('./views/Devices.vue'),
          meta: {
            title: 'meta.devices',
            middleware: [backup, cguAccept, devices],
          },
        },
        {
          path: 'snapshots',
          name: 'snapshots',
          component: () => import('./views/Snapshots.vue'),
          meta: {
            title: 'meta.snapshots',
            middleware: [backup, cguAccept, devices],
          },
        },
        {
          path: 'support',
          name: 'support',
          component: () => import('./views/Support.vue'),
          meta: {
            title: 'meta.support',
            middleware: [auth, cguAccept],
          },
        },
        {
          path: 'invoices',
          name: 'invoices',
          component: () => import('./views/Invoices.vue'),
          meta: {
            title: 'meta.invoices',
            middleware: [invoice, cguAccept],
          },
        },
        {
          path: 'administration',
          name: 'administration',
          component: () => import('./views/Administration.vue'),
          meta: {
            title: 'meta.administration',
            middleware: [isAdmin, cguAccept],
          },
        },
        {
          path: 'account',
          name: 'account',
          component: () => import('./views/Account.vue'),
          meta: {
            title: 'meta.account',
            middleware: [auth, cguAccept],
          },
        },
        {
          path: 'account/change-password',
          name: 'changePassword',
          component: () => import('./views/ChangePassword.vue'),
          meta: {
            title: 'meta.changePassword',
            middleware: [auth, cguAccept],
          },
        },
      ],
    },
    {
      path: '/terms-of-use',
      name: 'tcu',
      component: () => import('./views/Tcu.vue'),
      meta: {
        title: 'meta.terms',
      },
    },
    {
      path: '/:locale/*',
      name: '404',
      component: () => import('./views/404.vue'),
      meta: {
        title: 'Terms of use',
      },
    },
  ],
});


function nextFactory(context, middleware, index) {
  const subsequentMiddleware = middleware[index];
  // If no subsequent Middleware exists,
  // the default `next()` callback is returned.
  if (!subsequentMiddleware) return context.next;

  return (...parameters) => {
    // Run the default Vue Router `next()` callback first.
    context.next(...parameters);
    // Then run the subsequent Middleware with a new
    // `nextMiddleware()` callback.
    const nextMiddleware = nextFactory(context, middleware, index);
    subsequentMiddleware({ ...context, next: nextMiddleware });
  };
}

router.beforeEach((to, from, next) => {
  if (to.params.locale !== i18n.locale || !from.params.locale || from.params.locale !== to.params.locale) {
    const locale = to.params.locale;

    if (supportedLocalesInclude(locale)) {
      i18n.locale = locale;
      setDocumentLang(locale);
      setDocumentDirectionPerLocale(locale);
      store.dispatch('contents/setContents');
    } else {
      return next({ name: to.name, params: { locale: getStartingLocale() } });
    }
  }

  if (to.meta.middleware) {
    const middleware = Array.isArray(to.meta.middleware)
      ? to.meta.middleware
      : [to.meta.middleware];

    const context = {
      from,
      next,
      router,
      to,
    };
    const nextMiddleware = nextFactory(context, middleware, 1);

    return middleware[0]({ ...context, next: nextMiddleware });
  }

  return next();
});

export default router;

Vue.use(Router);
